/**
 * How wide a grid's columns are, and which of them are frozen.
 *
 * Which fields a view shows and in what order is every view type's question, so
 * it lives in `../views/field-order`; what is left here is the part only a grid
 * has. It is pure so the answer can be tested without rendering anything.
 */
import type { Field } from '@/api/types'
import { visibleFields, type FieldPlacement } from '../views/field-order'

/** One field's presentation in one grid. */
export interface GridFieldOptions extends FieldPlacement {
  width?: number
  /**
   * Which aggregation the footer shows under this column, and which one the
   * server is asked for.
   *
   * Two, because they are not always the same: eight of the nineteen are read
   * off `empty_count`. Empty strings rather than absent when the footer shows
   * nothing, which is what the endpoint stores to turn one off.
   */
  aggregation_type?: string
  aggregation_raw_type?: string
}

export interface GridColumn {
  field: Field
  width: number
  /** Frozen columns stay put while the rest scroll under them. */
  frozen: boolean
}

/** The backend's default, and the narrowest a column may be dragged. */
export const DEFAULT_COLUMN_WIDTH = 200
export const MIN_COLUMN_WIDTH = 78

/** The three row heights a grid view can choose between. */
export const ROW_HEIGHTS = { small: 33, medium: 55, large: 99 } as const
export type RowHeightSize = keyof typeof ROW_HEIGHTS

export const rowHeightFor = (size: RowHeightSize | undefined): number =>
  ROW_HEIGHTS[size ?? 'small']

/**
 * The columns to render, with the first `frozenCount` of them frozen.
 *
 * `frozen_column_count` includes the primary field and the backend caps it at
 * four, so the count is a count of *visible* columns from the left rather than a
 * set of field ids: hiding a frozen column promotes the next one, which is what
 * keeps the frozen section the same width whatever is hidden.
 */
export function gridColumns(
  fields: readonly Field[],
  options: Record<string, GridFieldOptions>,
  { frozenCount = 1 }: { frozenCount?: number } = {},
): GridColumn[] {
  return visibleFields(fields, options).map((field, index) => ({
    field,
    width: Math.max(
      MIN_COLUMN_WIDTH,
      options[String(field.id)]?.width ?? DEFAULT_COLUMN_WIDTH,
    ),
    frozen: index < frozenCount,
  }))
}

/**
 * Where each column starts, and how wide the whole row is.
 *
 * The offsets are what a frozen column's `left` is set to, so they have to be
 * measured from the same origin the sticky positioning uses -- the scroll
 * container's left edge, gutter included. The total is the row's width, and a
 * grid whose rows are narrower than its columns scrolls the header away from
 * the rows beneath it.
 */
export function columnLayout(
  columns: readonly GridColumn[],
  gutter: number,
): { offsets: number[]; width: number } {
  const offsets: number[] = []
  let x = gutter
  for (const column of columns) {
    offsets.push(x)
    x += column.width
  }
  return { offsets, width: x }
}
